import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { sollImZeitraum } from '@nkp/shared';
import { SuccessAnimation } from '../components/SuccessAnimation';
import { Bestaetigung } from '../components/Bestaetigung';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { cn } from '../lib/cn';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatDatum, formatEuro, formatZeitraum, parseEuroEingabe, parseZahlEingabe } from '../lib/format';
import {
  type AbrechnungsErgebnis,
  type AenderungsGrund,
  type Mietverhaeltnis,
  type Vertragsaenderung,
  GRUND_LABEL,
  heuteIso,
  vertragsverlauf,
  werteAm,
} from '../lib/haus';

/**
 * Vertragsänderungen ab Stichtag und Mieterkontakt in der Einheitenakte.
 *
 * Jede Änderung (Mieterhöhung, Anpassung der Vorauszahlungen, Personenzahl) gilt ab ihrem
 * Stichtag; frühere Monate bleiben unberührt. Abrechnung, Soll-Miete und Anlage V rechnen
 * je Monat mit dem jeweils gültigen Stand, die Personenumlage tagegenau.
 */

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

function centText(c: number): string {
  return (c / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ersterNaechsterMonat(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString().slice(0, 10);
}

function vortag(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

interface StaendeAntwort {
  staende: Array<Vertragsaenderung & { ab?: string }>;
}

function alsAenderungen(a: StaendeAntwort): Vertragsaenderung[] {
  return a.staende.map((x) => ({
    id: x.id,
    gueltigAb: x.gueltigAb ?? x.ab ?? '',
    kaltmieteCent: x.kaltmieteCent,
    vzNebenkostenCent: x.vzNebenkostenCent,
    vzHeizkostenCent: x.vzHeizkostenCent,
    personenzahl: x.personenzahl,
    grund: x.grund,
  }));
}

/**
 * Vorschlag zur Anpassung der Vorauszahlungen nach der letzten Abrechnung (§ 560 Abs. 4
 * BGB): Kosten der Abrechnung je belegtem Monat, auf volle Euro aufgerundet.
 */
export function vorauszahlungsVorschlag(
  mv: Mietverhaeltnis,
  ergebnisse: AbrechnungsErgebnis[],
): { ergebnis: AbrechnungsErgebnis; nkCent: number; heizCent: number; monate: number } | null {
  const letzte = ergebnisse
    .filter((e) => e.mietverhaeltnisId === mv.id && e.status !== 'offen' && e.kostenNkCent !== undefined)
    .sort((a, b) => b.zeitraumBis.localeCompare(a.zeitraumBis))[0];
  if (!letzte) return null;
  // Bereits nach dieser Abrechnung angepasst: kein erneuter Vorschlag.
  if ((mv.staende ?? []).some((s) => s.grund === 'vorauszahlung' && s.gueltigAb > letzte.zeitraumBis)) return null;
  const { monate } = sollImZeitraum(vertragsverlauf(mv), { von: letzte.zeitraumVon, bis: letzte.zeitraumBis });
  if (monate === 0) return null;
  const aufEuro = (c: number) => Math.ceil(c / monate / 100) * 100;
  return {
    ergebnis: letzte,
    nkCent: aufEuro(letzte.kostenNkCent ?? 0),
    heizCent: aufEuro(letzte.kostenHeizungCent ?? 0),
    monate,
  };
}

/* ------------------------------------------------------------------ Vertragsänderung */

export function VertragPanel({
  mv,
  ergebnisse,
  onGespeichert,
}: {
  mv: Mietverhaeltnis;
  ergebnisse: AbrechnungsErgebnis[];
  /** Neue Liste der Vertragsänderungen (auch in der Vorschau). */
  onGespeichert: (staende: Vertragsaenderung[], meldung: string) => void;
}) {
  const staende = useMemo(() => [...(mv.staende ?? [])].sort((a, b) => a.gueltigAb.localeCompare(b.gueltigAb)), [mv.staende]);
  const vorschlag = useMemo(() => vorauszahlungsVorschlag(mv, ergebnisse), [mv, ergebnisse]);
  const startDatum = (() => {
    const d = ersterNaechsterMonat();
    return mv.ende && d > mv.ende ? mv.ende : d <= mv.beginn ? tagNach(mv.beginn) : d;
  })();

  const [ab, setAb] = useState(startDatum);
  const [grund, setGrund] = useState<AenderungsGrund>('vorauszahlung');
  const [werte, setWerte] = useState(() => alsEingabe(werteAm(mv, vortag(startDatum))));
  const [geaendert, setGeaendert] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);
  const [pendente, setPendente] = useState<Vertragsaenderung[] | null>(null);
  const [entfernen, setEntfernen] = useState<Vertragsaenderung | null>(null);
  const [entfernenFehler, setEntfernenFehler] = useState<string | null>(null);
  const [entferntLaeuft, setEntferntLaeuft] = useState(false);

  // Ohne eigene Eingaben folgen die Felder dem Stand am Vortag des gewählten Stichtags.
  useEffect(() => {
    if (!geaendert && ab) setWerte(alsEingabe(werteAm(mv, vortag(ab))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ab, mv]);

  const bisher = ab ? werteAm(mv, vortag(ab)) : null;

  function feld(k: keyof typeof werte, v: string) {
    setGeaendert(true);
    setWerte((w) => ({ ...w, [k]: v }));
  }

  function vorschlagUebernehmen() {
    if (!vorschlag) return;
    setGrund('vorauszahlung');
    setGeaendert(true);
    setWerte((w) => ({ ...w, vzNk: centText(vorschlag.nkCent), vzHeiz: centText(vorschlag.heizCent) }));
  }

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!ab || ab <= mv.beginn) {
      setFehler(`Der Stichtag muss nach dem Mietbeginn (${formatDatum(mv.beginn)}) liegen.`);
      return;
    }
    if (mv.ende && ab > mv.ende) {
      setFehler(`Der Stichtag liegt nach dem Mietende (${formatDatum(mv.ende)}).`);
      return;
    }
    const kalt = parseEuroEingabe(werte.kalt);
    const vzNk = parseEuroEingabe(werte.vzNk);
    const vzHeiz = parseEuroEingabe(werte.vzHeiz);
    const personen = parseZahlEingabe(werte.personen);
    if (kalt === null || vzNk === null || vzHeiz === null || kalt < 0 || vzNk < 0 || vzHeiz < 0) {
      setFehler('Bitte gültige Beträge angeben (z. B. 180,00).');
      return;
    }
    if (personen === null || !Number.isInteger(personen) || personen < 1 || personen > 50) {
      setFehler('Die Personenzahl muss eine ganze Zahl zwischen 1 und 50 sein.');
      return;
    }
    if (
      bisher &&
      kalt === bisher.kaltmieteCent &&
      vzNk === bisher.vzNebenkostenCent &&
      vzHeiz === bisher.vzHeizkostenCent &&
      personen === bisher.personenzahl
    ) {
      setFehler('Die Werte entsprechen dem bisherigen Stand. Bitte mindestens einen Wert ändern.');
      return;
    }
    const daten = { gueltigAb: ab, kaltmieteCent: kalt, vzNebenkostenCent: vzNk, vzHeizkostenCent: vzHeiz, personenzahl: personen, grund };
    setSendet(true);
    try {
      let neu: Vertragsaenderung[];
      if (DEMO) {
        const eintrag: Vertragsaenderung = { id: `st-${Date.now()}`, ...daten };
        neu = [...staende.filter((s) => s.gueltigAb !== ab), eintrag].sort((a, b) => a.gueltigAb.localeCompare(b.gueltigAb));
      } else {
        neu = alsAenderungen(await api.post<StaendeAntwort>(`/mietverhaeltnisse/${mv.id}/staende`, daten));
      }
      setPendente(neu);
      setFertig(`${GRUND_LABEL[grund]} ab ${formatDatum(ab)} erfasst.`);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Vertragsänderung konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  async function standEntfernen() {
    if (!entfernen) return;
    setEntferntLaeuft(true);
    setEntfernenFehler(null);
    try {
      const neu = DEMO
        ? staende.filter((s) => s.id !== entfernen.id)
        : alsAenderungen(await api.del<StaendeAntwort>(`/mietverhaeltnisse/${mv.id}/staende/${entfernen.id}`));
      const text = `Vertragsänderung ab ${formatDatum(entfernen.gueltigAb)} entfernt.`;
      setEntfernen(null);
      onGespeichert(neu, text);
    } catch (err) {
      setEntfernenFehler(err instanceof ApiFehler ? err.message : 'Die Vertragsänderung konnte nicht entfernt werden.');
    } finally {
      setEntferntLaeuft(false);
    }
  }

  if (fertig) {
    return (
      <div className="mt-4 border-t border-border pt-4">
        <SuccessAnimation
          titel="Vertragsänderung erfasst"
          beschreibung={fertig}
          groesse={64}
          onFertig={() => onGespeichert(pendente ?? staende, fertig)}
        />
      </div>
    );
  }

  const zeilen: Array<{ id: string | null; ab: string; titel: string; w: ReturnType<typeof werteAm>; vorher: ReturnType<typeof werteAm> | null; eintrag: Vertragsaenderung | null }> = [
    { id: null, ab: mv.beginn, titel: 'Mietbeginn', w: werteAm({ ...mv, staende: [] }, mv.beginn), vorher: null, eintrag: null },
    ...staende.map((s) => ({
      id: s.id,
      ab: s.gueltigAb,
      titel: GRUND_LABEL[s.grund] ?? 'Vertragsänderung',
      w: { kaltmieteCent: s.kaltmieteCent, vzNebenkostenCent: s.vzNebenkostenCent, vzHeizkostenCent: s.vzHeizkostenCent, personenzahl: s.personenzahl },
      vorher: werteAm(mv, vortag(s.gueltigAb)),
      eintrag: s,
    })),
  ];
  const heute = heuteIso();
  const gueltigIndex = zeilen.reduce((acc, z, i) => (z.ab <= heute ? i : acc), 0);

  return (
    <div className="mt-4 space-y-5 border-t border-border pt-4">
      <div>
        <p className="mb-2 text-sm font-semibold text-fg">Vertragsverlauf</p>
        <ol className="space-y-2">
          {zeilen.map((z, i) => (
            <li
              key={z.id ?? 'beginn'}
              className={cn(
                'rounded-lg border px-3 py-2.5',
                i === gueltigIndex ? 'border-accent bg-accent-soft/40' : 'border-border bg-surface',
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-fg tabellenziffern">ab {formatDatum(z.ab)}</span>
                  <span className="text-sm text-fg-muted">{z.titel}</span>
                  {i === gueltigIndex && <Badge ton="info">aktuell gültig</Badge>}
                  {z.ab > heute && <Badge ton="neutral">künftig</Badge>}
                </div>
                {z.eintrag && (
                  <button
                    type="button"
                    onClick={() => {
                      setEntfernenFehler(null);
                      setEntfernen(z.eintrag);
                    }}
                    className="rounded-md text-xs font-medium text-fg-muted hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                  >
                    Entfernen
                  </button>
                )}
              </div>
              <dl className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
                <Wert label="Kaltmiete" wert={formatEuro(z.w.kaltmieteCent)} neu={!!z.vorher && z.vorher.kaltmieteCent !== z.w.kaltmieteCent} />
                <Wert label="VZ Nebenkosten" wert={formatEuro(z.w.vzNebenkostenCent)} neu={!!z.vorher && z.vorher.vzNebenkostenCent !== z.w.vzNebenkostenCent} />
                <Wert label="VZ Heizkosten" wert={formatEuro(z.w.vzHeizkostenCent)} neu={!!z.vorher && z.vorher.vzHeizkostenCent !== z.w.vzHeizkostenCent} />
                <Wert label="Personen" wert={String(z.w.personenzahl)} neu={!!z.vorher && z.vorher.personenzahl !== z.w.personenzahl} />
              </dl>
            </li>
          ))}
        </ol>
      </div>

      {vorschlag && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-fg">
            <span className="font-medium">Vorschlag aus der Abrechnung {formatZeitraum(vorschlag.ergebnis.zeitraumVon, vorschlag.ergebnis.zeitraumBis)}:</span>{' '}
            <span className="text-fg-muted">
              VZ Nebenkosten {formatEuro(vorschlag.nkCent)}, VZ Heizkosten {formatEuro(vorschlag.heizCent)} je Monat
              (Kosten ÷ {vorschlag.monate} Monate, aufgerundet).
            </span>
          </p>
          <Button groesse="sm" variante="sekundaer" className="shrink-0" onClick={vorschlagUebernehmen}>
            Übernehmen
          </Button>
        </div>
      )}

      <form onSubmit={speichern} className="space-y-4">
        <p className="text-sm font-semibold text-fg">Änderung erfassen</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Gültig ab" type="date" value={ab} onChange={(e) => setAb(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Anlass</span>
            <select className={feldKlasse} value={grund} onChange={(e) => setGrund(e.target.value as AenderungsGrund)}>
              {(Object.keys(GRUND_LABEL) as AenderungsGrund[]).map((g) => (
                <option key={g} value={g}>
                  {GRUND_LABEL[g]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Input label="Kaltmiete" einheit="€" inputMode="decimal" value={werte.kalt} onChange={(e) => feld('kalt', e.target.value)} />
          <Input label="VZ Nebenkosten" einheit="€" inputMode="decimal" value={werte.vzNk} onChange={(e) => feld('vzNk', e.target.value)} />
          <Input label="VZ Heizkosten" einheit="€" inputMode="decimal" value={werte.vzHeiz} onChange={(e) => feld('vzHeiz', e.target.value)} />
          <Input label="Personen" inputMode="numeric" value={werte.personen} onChange={(e) => feld('personen', e.target.value)} />
        </div>
        <p className="text-xs leading-relaxed text-fg-muted">{HINWEIS[grund]}</p>
        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" ladend={sendet}>
            Änderung speichern
          </Button>
          {geaendert && (
            <Button
              type="button"
              variante="dezent"
              onClick={() => {
                setGeaendert(false);
                setWerte(alsEingabe(werteAm(mv, vortag(ab))));
              }}
            >
              Werte zurücksetzen
            </Button>
          )}
        </div>
      </form>

      <Bestaetigung
        offen={entfernen !== null}
        titel="Vertragsänderung entfernen?"
        aktion="Entfernen"
        ladend={entferntLaeuft}
        fehler={entfernenFehler}
        onBestaetigen={() => void standEntfernen()}
        onAbbrechen={() => setEntfernen(null)}
      >
        {entfernen && (
          <p>
            Die Änderung ab {formatDatum(entfernen.gueltigAb)} ({GRUND_LABEL[entfernen.grund]}) wird entfernt. Ab diesem Tag
            gelten wieder die vorherigen Werte — für Abrechnung, Soll-Miete und Anlage V.
          </p>
        )}
      </Bestaetigung>
    </div>
  );
}

const HINWEIS: Record<AenderungsGrund, string> = {
  vorauszahlung:
    'Nach einer Abrechnung kann jede Vertragspartei die Vorauszahlungen durch Erklärung in Textform auf eine angemessene Höhe anpassen (§ 560 Abs. 4 BGB). Die neuen Beträge gelten ab dem gewählten Stichtag.',
  mieterhoehung:
    'Eine Mieterhöhung bis zur ortsüblichen Vergleichsmiete bedarf der Zustimmung des Mieters; sie wirkt ab Beginn des dritten Kalendermonats nach Zugang des Erhöhungsverlangens (§§ 558, 558b BGB).',
  personen:
    'Die Personenzahl wird tagegenau gewichtet. Kosten nach dem Schlüssel „Personen“ werden ab dem Stichtag mit der neuen Zahl verteilt.',
  sonstiges: 'Die Werte gelten ab dem Stichtag. Frühere Monate bleiben unverändert.',
};

function alsEingabe(w: ReturnType<typeof werteAm>) {
  return {
    kalt: centText(w.kaltmieteCent),
    vzNk: centText(w.vzNebenkostenCent),
    vzHeiz: centText(w.vzHeizkostenCent),
    personen: String(w.personenzahl),
  };
}

function tagNach(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function Wert({ label, wert, neu }: { label: string; wert: string; neu: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className={cn('font-semibold tabellenziffern', neu ? 'text-accent' : 'text-fg')}>{wert}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ Mieterkontakt */

interface MieterVoll {
  id: string;
  anrede: string | null;
  vorname: string | null;
  nachname: string;
  email: string | null;
  telefon: string | null;
  iban: string | null;
  ibanMaskiert: string | null;
  notizen: string | null;
}

function demoMieter(id: string, name: string): MieterVoll {
  const teile = name.trim().split(/\s+/);
  const nachname = teile.length > 1 ? teile[teile.length - 1] : name;
  const vorname = teile.length > 1 ? teile.slice(0, -1).join(' ') : null;
  const kurz = `${(vorname ?? 'mieter').toLowerCase().replace(/[^a-z]/g, '')}.${nachname.toLowerCase().replace(/[^a-z]/g, '')}`;
  return {
    id,
    anrede: null,
    vorname,
    nachname,
    email: `${kurz}@beispiel.de`,
    telefon: '0151 2345678',
    iban: 'DE02120300000000202051',
    ibanMaskiert: 'DE … 2051',
    notizen: null,
  };
}

function maskiere(iban: string | null): string {
  if (!iban) return '–';
  return `${iban.slice(0, 2)} … ${iban.slice(-4)}`;
}

export function MieterKontaktPanel({
  mieterId,
  name,
  onGespeichert,
}: {
  mieterId: string;
  name: string;
  onGespeichert: (name: string) => void;
}) {
  const [m, setM] = useState<MieterVoll | null>(DEMO ? demoMieter(mieterId, name) : null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [form, setForm] = useState({ anrede: '', vorname: '', nachname: '', email: '', telefon: '', iban: '' });
  const [sendet, setSendet] = useState(false);
  const [ibanSichtbar, setIbanSichtbar] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);

  useEffect(() => {
    if (DEMO) return;
    api
      .get<{ mieter: MieterVoll }>(`/mieter/${mieterId}`)
      .then((d) => setM(d.mieter))
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Mieterdaten konnten nicht geladen werden.'));
  }, [mieterId]);

  function starten() {
    if (!m) return;
    setForm({
      anrede: m.anrede ?? '',
      vorname: m.vorname ?? '',
      nachname: m.nachname,
      email: m.email ?? '',
      telefon: m.telefon ?? '',
      iban: m.iban ?? '',
    });
    setFehler(null);
    setGespeichert(false);
    setBearbeiten(true);
  }

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!m) return;
    setFehler(null);
    if (!form.nachname.trim()) {
      setFehler('Bitte den Nachnamen bzw. die Firma angeben.');
      return;
    }
    const daten = {
      anrede: form.anrede.trim() || null,
      vorname: form.vorname.trim() || null,
      nachname: form.nachname.trim(),
      email: form.email.trim() || null,
      telefon: form.telefon.trim() || null,
      iban: form.iban.replace(/\s+/g, '').toUpperCase(),
    };
    setSendet(true);
    try {
      const neu = DEMO
        ? { ...m, ...daten, iban: daten.iban || null, ibanMaskiert: daten.iban ? maskiere(daten.iban) : null }
        : (await api.patch<{ mieter: MieterVoll }>(`/mieter/${m.id}`, daten)).mieter;
      setM(neu);
      setBearbeiten(false);
      setGespeichert(true);
      onGespeichert([neu.vorname, neu.nachname].filter(Boolean).join(' '));
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Mieterdaten konnten nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  if (!m) {
    return (
      <div className="mt-4 border-t border-border pt-4">
        {fehler ? <p className="text-sm text-danger">{fehler}</p> : <p className="text-sm text-fg-muted">Wird geladen …</p>}
      </div>
    );
  }

  if (bearbeiten) {
    const setze = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
    return (
      <form onSubmit={speichern} className="mt-4 space-y-4 border-t border-border pt-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[8rem_1fr_1fr]">
          <Input label="Anrede" value={form.anrede} onChange={setze('anrede')} placeholder="Frau / Herr" />
          <Input label="Vorname" value={form.vorname} onChange={setze('vorname')} autoComplete="off" />
          <Input label="Nachname / Firma" value={form.nachname} onChange={setze('nachname')} autoComplete="off" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="E-Mail" type="email" inputMode="email" value={form.email} onChange={setze('email')} autoComplete="off" hinweis="Für den Versand der Abrechnung." />
          <Input label="Telefon" type="tel" inputMode="tel" value={form.telefon} onChange={setze('telefon')} autoComplete="off" />
        </div>
        <Input
          label="IBAN des Mieters"
          value={form.iban}
          onChange={setze('iban')}
          autoComplete="off"
          spellCheck={false}
          hinweis="Für Rückzahlungen von Guthaben und den Zahlungsabgleich. Wird in Listen nur gekürzt angezeigt."
        />
        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" ladend={sendet}>
            Speichern
          </Button>
          <Button type="button" variante="dezent" onClick={() => setBearbeiten(false)}>
            Abbrechen
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      {gespeichert && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          role="status"
          className="mb-3 rounded-lg bg-success-soft px-3 py-2 text-sm text-success-soft-fg"
        >
          Mieterdaten gespeichert.
        </motion.p>
      )}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <Kontakt label="Name" wert={[m.anrede, m.vorname, m.nachname].filter(Boolean).join(' ')} />
        <Kontakt
          label="E-Mail"
          wert={m.email ? <a className="text-accent hover:underline" href={`mailto:${m.email}`}>{m.email}</a> : '–'}
        />
        <Kontakt
          label="Telefon"
          wert={m.telefon ? <a className="text-accent hover:underline" href={`tel:${m.telefon.replace(/\s+/g, '')}`}>{m.telefon}</a> : '–'}
        />
        <Kontakt
          label="IBAN"
          wert={
            m.iban ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span className="tabellenziffern">{ibanSichtbar ? m.iban.replace(/(.{4})/g, '$1 ').trim() : maskiere(m.iban)}</span>
                <button
                  type="button"
                  onClick={() => setIbanSichtbar((s) => !s)}
                  className="rounded-md text-xs font-medium text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  {ibanSichtbar ? 'Verbergen' : 'Anzeigen'}
                </button>
              </span>
            ) : (
              '–'
            )
          }
        />
      </dl>
      {!m.email && (
        <p className="mt-3 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-soft-fg">
          Keine E-Mail-Adresse hinterlegt. Die Abrechnung kann dann nur per Post oder Download übermittelt werden.
        </p>
      )}
      {fehler && <p className="mt-3 text-sm text-danger">{fehler}</p>}
      <Button className="mt-4" groesse="sm" variante="sekundaer" onClick={starten}>
        Kontaktdaten bearbeiten
      </Button>
    </div>
  );
}

function Kontakt({ label, wert }: { label: string; wert: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-fg-subtle">{label}</dt>
      <dd className="mt-0.5 truncate text-sm text-fg">{wert}</dd>
    </div>
  );
}
